import axios from "axios";
import { getKoreaDate } from "../utils/kpc";

// KPC 자동로그인에만 사용하는 브라우저 저장 키다.
// 다른 택배사의 로그인 정보와 섞이지 않도록 KPC 전용 이름을 사용한다.
export const KPC_LOGIN_KEY_STORAGE = "kpcLoginKey";
export const KPC_USER_STORAGE = "kpcUser";
export const KPC_AUTO_LOGIN_STORAGE = "kpcAutoLoginEnabled";

// 자동로그인 요청은 일반 KPC 인스턴스의 401 인터셉터를 통과시키지 않는다.
// /watcher/sign 자체를 재시도하면 무한 반복될 수 있기 때문이다.
const KpcAuthInstance = axios.create({
  baseURL: process.env.REACT_APP_KPC_API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

let kpcAutoLoginPromise = null;
let kpcBootstrapPromise = null;

export const isKpcAutoLoginEnabled = () =>
  window.localStorage.getItem(KPC_AUTO_LOGIN_STORAGE) === "true";

export const hasKpcAutoLoginCredentials = () =>
  isKpcAutoLoginEnabled() &&
  Boolean(window.localStorage.getItem(KPC_LOGIN_KEY_STORAGE));

// 자동로그인을 명시적으로 해제하거나 재인증에 실패하면 관련 저장값을 모두 제거한다.
export const clearKpcAutoLoginCredentials = () => {
  window.localStorage.removeItem(KPC_LOGIN_KEY_STORAGE);
  window.localStorage.removeItem(KPC_USER_STORAGE);
  window.localStorage.removeItem(KPC_AUTO_LOGIN_STORAGE);
};

// 일반 로그인 및 자동로그인 성공 응답의 최신 키와 사용자 정보를 함께 저장한다.
// 자동로그인 성공 시 기존 키가 즉시 무효화되므로 새 login_key 저장이 필수다.
export const saveKpcAutoLoginCredentials = (user) => {
  if (!user?.login_key) {
    clearKpcAutoLoginCredentials();
    return false;
  }

  window.localStorage.setItem(KPC_LOGIN_KEY_STORAGE, user.login_key);
  window.localStorage.setItem(KPC_USER_STORAGE, JSON.stringify(user));
  window.localStorage.setItem(KPC_AUTO_LOGIN_STORAGE, "true");
  return true;
};

// 자동로그인 응답에 포함되지 않은 관리자 여부 등 화면용 메타정보를 보존한다.
const getStoredKpcUser = () => {
  try {
    return JSON.parse(window.localStorage.getItem(KPC_USER_STORAGE)) || {};
  } catch {
    return {};
  }
};

// 서버 사용자 응답을 기존 Recoil 로그인 상태 형식으로 변환한다.
// KPC 응답에는 지점명이 없을 수 있으므로 점소코드를 대체 표기로 사용한다.
export const createAuthenticatedKpcLoginState = (previousLogin, user) => ({
  ...previousLogin,
  isLogin: true,
  company: "KPC",
  userId: user.user_id || previousLogin.userId || "",
  branchName: user.bran_exp || user.bran_cd || "",
  branchCode: user.bran_cd || "",
  saId: user.sa_id || "",
  accountId: user.account_id || "",
  camUsable: user.cam_usable || "",
  camStatus: user.cam_status || "",
  nvrUsable: user.nvr_usable || "0",
  isAdmin: Boolean(user.isAdmin || user.is_admin),
  kpcLoginDate: getKoreaDate(),
});

// KPC 인증 실패 시 ID 저장 설정과 선택 택배사는 유지하고 인증 정보만 비운다.
export const createLoggedOutKpcLoginState = (previousLogin) => ({
  ...previousLogin,
  isLogin: false,
  userId: previousLogin.isUserIdStored ? previousLogin.userId : "",
  branchName: "",
  branchCode: "",
  localIP: "",
  saId: "",
  accountId: "",
  camUsable: "",
  nvrUsable: "0",
  isAdmin: false,
  camStatus: "",
  kpcLoginDate: "",
});

const executeKpcAutoLogin = async () => {
  const loginKey = window.localStorage.getItem(KPC_LOGIN_KEY_STORAGE);

  if (!isKpcAutoLoginEnabled() || !loginKey) {
    return null;
  }

  try {
    const response = await KpcAuthInstance.post("/watcher/sign", {
      api: "autoLogin",
      data: [{ login_key: loginKey }],
    });
    const responseData = response.data;

    if (responseData?.result !== "00" || !responseData?.data?.[0]) {
      clearKpcAutoLoginCredentials();
      return null;
    }

    const user = {
      ...getStoredKpcUser(),
      ...responseData.data[0],
    };

    if (!saveKpcAutoLoginCredentials(user)) {
      return null;
    }

    return user;
  } catch {
    clearKpcAutoLoginCredentials();
    return null;
  }
};

// 동시에 여러 API가 401을 반환해도 자동로그인은 하나의 Promise만 공유한다.
export const refreshKpcSession = () => {
  if (!kpcAutoLoginPromise) {
    kpcAutoLoginPromise = executeKpcAutoLogin().finally(() => {
      kpcAutoLoginPromise = null;
    });
  }

  return kpcAutoLoginPromise;
};

// 앱 최초 진입은 개발 모드에서 effect가 다시 실행되어도 같은 결과를 재사용한다.
// 동일한 로그인 키로 자동로그인을 중복 호출해 한 요청이 실패하는 상황을 막는다.
export const initializeKpcAuthentication = () => {
  if (!kpcBootstrapPromise) {
    kpcBootstrapPromise = refreshKpcSession();
  }

  return kpcBootstrapPromise;
};
